import React, { useEffect, useState, useRef } from 'react';
import SendToPreparingToSignInternal from './sendToPreparingToSignInternal';
import SendToPreparingToSignInternalSlideout from './SendToPreparingToSignInternalSlideout';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';

import PlaceholderWarningModal from '../../../../document-wizard-modal/custom-modals/placeholderWarningModal/placeholderWarningModal';

interface Props {
  dealId: string;
  teamId: string;
  documentId: string;
  isOpen?: boolean; // Optional - when provided, used for external control (modal management only)
  onClose?: () => void; // Called when slideout is closed (to update Redux state)
  handleSaveDocument?: (() => Promise<void>) | undefined;
  placeholdersToComplete?: number;
  onModalOpen?: () => void;
  onParentModalClose?: () => void;
  onActionTriggered?: (action: () => void) => void;
  dropdownLabel?: string;
}

const SendToPreparingToSignInternalContainer = ({
  dealId,
  documentId,
  teamId,
  isOpen,
  onClose,
  handleSaveDocument,
  placeholdersToComplete = 0,
  onModalOpen,
  onParentModalClose,
  onActionTriggered,
  dropdownLabel,
}: Props) => {
  const [showModal, setShowModal] = useState(false);

  const [showWarningModal, setShowWarningModal] = useState(false);

  const { state } = useDocumentState();
  // Use database-backed suggestions (not CKEditor in-memory state)
  // to reliably check for pending suggestions
  const documentSuggestions = state.documentSuggestions;
  const hasPendingSuggestions = documentSuggestions.filter((s) => s?.status === 'pending').length > 0;

  const isEligible = state.isEligibleForSigningPrep;

  const isDisabled = hasPendingSuggestions || !isEligible;

  // Track component mount state to prevent memory leaks
  const isMountedRef = useRef(true);

  // Track if we've processed the external open request to prevent duplicate modals
  const hasProcessedExternalOpenRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle external isOpen prop changes (from Redux/parent component)
  useEffect(() => {
    if (isOpen !== undefined) {
      // Only process when transitioning to open, not on every prop change
      if (isOpen && !hasProcessedExternalOpenRef.current) {
        hasProcessedExternalOpenRef.current = true;
        if (placeholdersToComplete > 0) {
          setShowWarningModal(true);
        } else {
          setShowModal(true);
        }
      } else if (!isOpen) {
        // Reset when closing
        hasProcessedExternalOpenRef.current = false;
        setShowModal(false);
        setShowWarningModal(false);
      }
    }
  }, [isOpen, placeholdersToComplete]);

  const handleWarningModalClose = () => {
    if (isMountedRef.current) {
      setShowWarningModal(false);
      setShowModal(false);
      onClose?.(); // Close external modal if provided
    }
  };

  const handleSlideoutClose = () => {
    if (isMountedRef.current) {
      setShowModal(false);
      onClose?.(); // Close external modal if provided
    }
  };

  return (
    <>
      {/* Only render button if not being used purely for modal management (when isOpen prop is provided) */}
      {isOpen === undefined && (
        <SendToPreparingToSignInternal
          disabled={isDisabled}
          onClick={() => {
            if (isMountedRef.current) {
              const action = () => {
                onParentModalClose?.(); // Close parent "Before You Go" modal
                if (placeholdersToComplete < 1) {
                  setShowModal(true);
                  onModalOpen?.();
                } else {
                  setShowWarningModal(true);
                  onModalOpen?.();
                }
              };

              if (onActionTriggered) {
                onActionTriggered(action);
              } else {
                action();
              }
            }
          }}
          showReturnForReviewTooltip={hasPendingSuggestions}
          dropdownLabel={dropdownLabel}
        />
      )}
      <PlaceholderWarningModal
        isOpen={showWarningModal}
        placeholders={placeholdersToComplete}
        onContinue={() => {
          if (isMountedRef.current) {
            setShowWarningModal(false);
            // Delay slideout opening to ensure modal closes first
            setTimeout(() => {
              setShowModal(true);
            }, 100);
            // Note: onModalOpen already called when warning modal opened, no need to call again
          }
        }}
        onReview={handleWarningModalClose}
        onHide={handleWarningModalClose}
      />
      <SendToPreparingToSignInternalSlideout
        documentId={documentId}
        dealId={dealId}
        teamId={teamId}
        onClose={handleSlideoutClose}
        isOpen={showModal}
        handleSaveDocument={handleSaveDocument}
      />
    </>
  );
};

export default SendToPreparingToSignInternalContainer;
